import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui";

interface DataItem {
  label: string;
  value: number;
  color: string;
}

interface AnalyticsDonutChartProps {
  title: string;
  data: DataItem[];
  className?: string;
  size?: number;
  thickness?: number;
}

export const AnalyticsDonutChart: React.FC<AnalyticsDonutChartProps> = ({
  title,
  data,
  className = "",
  size = 200,
  thickness = 40,
}) => {
  // Calculate total for percentages
  const total = data.reduce((sum, item) => sum + item.value, 0);

  // Calculate the center of the donut
  const center = size / 2;
  // Calculate the radius of the donut
  const radius = center - 10; // 10px margin
  // Calculate the inner radius (to create the hole)
  const innerRadius = radius - thickness;

  // Calculate the circumference of the circle
  const circumference = 2 * Math.PI * radius;

  // Generate the segments
  let currentAngle = 0;
  const segments = data.map((item, index) => {
    // Calculate the angle for this segment
    const angle = (item.value / total) * 360;

    // Convert to radians for calculations
    const startAngle = currentAngle * (Math.PI / 180);
    currentAngle += angle;
    const endAngle = currentAngle * (Math.PI / 180);

    // Calculate arc points
    const startX = center + radius * Math.sin(startAngle);
    const startY = center - radius * Math.cos(startAngle);
    const endX = center + radius * Math.sin(endAngle);
    const endY = center - radius * Math.cos(endAngle);

    // Large arc flag is 1 if angle > 180 degrees
    const largeArcFlag = angle > 180 ? 1 : 0;

    // Create the path for the arc
    const path = [
      `M ${center + innerRadius * Math.sin(startAngle)},${
        center - innerRadius * Math.cos(startAngle)
      }`, // Start at inner radius
      `L ${startX},${startY}`, // Line to outer radius
      `A ${radius},${radius} 0 ${largeArcFlag},1 ${endX},${endY}`, // Arc to end point
      `L ${center + innerRadius * Math.sin(endAngle)},${
        center - innerRadius * Math.cos(endAngle)
      }`, // Line to inner radius
      `A ${innerRadius},${innerRadius} 0 ${largeArcFlag},0 ${
        center + innerRadius * Math.sin(startAngle)
      },${center - innerRadius * Math.cos(startAngle)}`, // Arc back to start
      "Z", // Close path
    ].join(" ");

    return (
      <path
        key={index}
        d={path}
        fill={item.color}
        stroke="white"
        strokeWidth="1"
      />
    );
  });

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center">
        <div className="relative" style={{ width: size, height: size }}>
          <svg width={size} height={size}>
            {segments}
          </svg>
          {/* Optional: Display total in center */}
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-lg font-bold">{total.toLocaleString()}</span>
          </div>
        </div>

        {/* Legend */}
        <div className="mt-4 grid grid-cols-2 gap-2 w-full">
          {data.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: item.color }}
              />
              <div className="flex justify-between w-full">
                <span className="text-sm">{item.label}</span>
                <span className="text-sm text-muted-foreground">
                  {Math.round((item.value / total) * 100)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
